import { message } from "antd";
import memoizeOne from "memoize-one";
import queryString from "query-string";
import { PureComponent, useContext } from "react";
import { Route, type RouteComponentProps } from "react-router";

import { cn } from "@btnet/utilities-core";

import { AppContext, AppProvider } from "helpers/AppProvider";
import { AppProviderHandler } from "helpers/AppProvider.api.handler";
import {
    type AppDefaultInfo,
    type BuilderInfo,
    type EnvironmentInfo,
    type UserInfo,
} from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { BTLinkSpaRoutingProvider } from "helpers/globalContext/BTLinkSpaRoutingProvider";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { EnvironmentInfoContext } from "helpers/globalContext/EnvironmentInfoContext";
import { DefaultEnabledSpaInfo, SpaInfoContext } from "helpers/globalContext/SPAInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { type IAPIHandlerResult } from "types/apiResponse/apiResponse";
import { BTLocalStorage, LocalStorageKeys } from "types/btStorage";
import { CommonConstants } from "types/constants";
import { BTLoginTypes, ForbiddenReason, JobStatusTypes, type MenuItemName } from "types/enum";

import { useTracking } from "utilities/analytics/analytics";
import {
    APIError,
    handleNoJobAccessError,
    handleSubscriptionInactiveError,
    NoAccessError,
    showAPIErrorMessage,
} from "utilities/apiHandler";
import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { getSelectedValue } from "utilities/form/form";
import {
    clearSelectedJobsiteIdWithBuilder,
    filterJobsList,
    getJobPickerState,
    getSelectedJobId,
    getSelectedJobIdsForJobPicker,
    getSelectedJobsiteIdWithBuilder,
    isSpecialJob,
    removeJobPickerState,
    setJobPickerState,
} from "utilities/jobPicker/jobPicker";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { EmptyStateEntity } from "utilities/list/list.types";
import {
    getCurrentPortalType,
    getLoginTypeFromCurrentPortal,
    getLoginTypeFromPortal,
    getPortalTypeForUrl,
    isInPortal,
    PortalType,
} from "utilities/portal/portal";
import { getBaseRoute, routes } from "utilities/routes";
import { clearQueryString } from "utilities/url/url";

//
import { optInEveryLoginAndLogout } from "commonComponents/auth/AuthStoreDiscovery.utils";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    EmptyStateHandler,
    type IEmptyStateHandler,
} from "commonComponents/entity/emptyState/common/EmptyState.api.handler";
import { RouteJob } from "commonComponents/entity/job/RouteJob/RouteJob";
import { JobStatusFilterTypes } from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import { mapJobStatusToJobStatusFilterTypes } from "commonComponents/entity/jobAccess/JobAccess/JobAccess.utils";
import { ErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { HotkeyDisplaySetup } from "commonComponents/helpers/HotkeyDisplaySetup/HotkeyDisplaySetup";
import { AcceptTermsAndPolicies } from "commonComponents/utilities/AcceptTermsAndPolicies/acceptTermsAndPolicies/AcceptTermsAndPolicies";
import { AcceptTermsAndPoliciesHandler } from "commonComponents/utilities/AcceptTermsAndPolicies/acceptTermsAndPolicies/AcceptTermsAndPolicies.api.handler";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { OwnerHeaderInfo } from "commonComponents/utilities/HeaderInfo/OwnerHeaderInfo";
import { type IHeaderInfoResponse } from "commonComponents/utilities/HeaderInfo/OwnerHeaderInfo.api.types";
import { JobPicker } from "commonComponents/utilities/JobPicker/JobPicker";
import {
    type IJobPickerHandler,
    JobPickerHandler,
} from "commonComponents/utilities/JobPicker/JobPicker.api.handler";
import {
    type AccountSwitcherItem,
    type IApiJobBuilderRequest,
    type IApiJobFilterRequest,
    JobPickerFilterTypes,
    JobPickerJob,
    type JobPickerLoadResponse,
    type JobPickerPermissions,
    type JobPickerSetResponse,
} from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import { default as JobPickerFilterResponse } from "commonComponents/utilities/JobPicker/JobPicker.filter.api.json";
import {
    type IJobPickerPersistantState,
    JobIdTypes,
    JobPickerDisplayModes,
    JobPickerSelectModes,
    JobSortOptions,
} from "commonComponents/utilities/JobPicker/JobPicker.types";
import {
    clearJobPickerKeys,
    getCollapsed,
    getJobPickerStorageKey,
    getStoredJobsOtherFilter,
} from "commonComponents/utilities/JobPicker/JobPicker.utils";
import { ResizableJobPickerLoading } from "commonComponents/utilities/JobPicker/Sections/ResizableJobPickerLoading/ResizableJobPickerLoading";
import { GetEphDaysRemaining } from "commonComponents/utilities/MainNavigation/common/EphInfo/EphInfo";
import PublicEphBanner from "commonComponents/utilities/MainNavigation/common/EphInfo/PublicEphBanner";
import { MainNavigation } from "commonComponents/utilities/MainNavigation/MainNavigation";
import { MfaEnrollmentNudgeBanner } from "commonComponents/utilities/MfaEnrollmentNudgeBanner/MfaEnrollmentNudgeBanner";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import { FilterHandler, type IFilterHandler } from "entity/filters/Filter/Filter.api.handler";
import {
    FilterEntity,
    FilterEntityType,
    type IFilterFormValues,
    SelectedFilterItem,
} from "entity/filters/Filter/Filter.api.types";
import {
    getFilterString,
    getFilterStringFromItems,
    getSelectedFilterItems,
} from "entity/filters/filters.utils";
import { JobHandler } from "entity/job/Job.api.handler";
import { getLastPageRequested, handleLogout } from "entity/login/Login/Login.utils";
import { RouteMessageCompose } from "entity/message/MessageCompose/MessageComposeRoute";
import { RouteTemplate } from "entity/template/Template/RouteTemplate";
import { RouteTemplateFromTemplate } from "entity/template/TemplateFromTemplate/RouteTemplateFromTemplate";
import { TemplateListTabs } from "entity/template/TemplateList/TemplateListTab/TemplateListTab.api.types";

import "./ListPageLayout.less";

import { JobStatus } from "../entity/job/Job.api.types";
import {
    filterGeneralJob,
    getFallbackSelectedJobId,
    isJobPickerInitialLoad,
} from "./ListPageLayout.utils";

const FrozenBuilder = lazyLoadComponent(
    () => import("entity/FrozenBuilder/FrozenBuilderListPageLayout"),
);

const jobFilters = new FilterEntity(JobPickerFilterResponse);

export interface IListPageChildProps {
    builderId: number;
    jobIds: number[];
    generalJobId?: number;
    jobName: string;
    key: number;
    userId: number;
    isTemplateMode: boolean;
    loginType: BTLoginTypes;
    jobPickerReady: boolean;
    setSelectedJobId?: (jobId: number, orgId?: number, loadMissingJobs?: boolean) => Promise<void>;
    isAllJobsSelected: boolean;
    jobPickerCount: number;
    setJobPickerCollapsed: (isCollapsed: boolean) => void;
    selectedFilters?: SelectedFilterItem[];
}

export interface IListPageLayoutProps {
    path?: string;
    exact?: boolean;
    filterHandler?: IFilterHandler;
    /** The component to render within the content section of the page */
    component: (listProps: IListPageChildProps, route: RouteComponentProps<any>) => React.ReactNode;
    jobPickerHandler?: IJobPickerHandler;
    jobHandler?: JobHandler;
    /**
     * Show or hide the job picker
     * @default true
     */
    showJobPicker?: boolean;
    selectedMenuItem: MenuItemName;

    /**
     * The job picker display mode, required if showJobPicker is true
     */
    jobPickerDisplayMode?: JobPickerDisplayModes;
    emptyStateHandler?: IEmptyStateHandler;
    appProviderHandler?: AppProviderHandler;
    /**
     * Single select to require a job be selected or
     * Multi select to all the selection of all jobs
     * @default JobPickerSelectModes.Multiple
     */
    jobPickerSelectMode?: JobPickerSelectModes;
    /**
     * If set to true, keep query params in the URL. Avoid using this unless you know what you are doing as query params
     * breaks recursive routing
     */
    keepQueryParams?: boolean;

    /**
     * If set to true, do not show the owner header info. This prop can be relocated to a client specific layout component in the future
     */
    hideOwnerHeaderInfo?: boolean;

    /**
     * Allows the general job to appear in the job picker
     */
    includeGeneralJob?: boolean;

    /**
     * When true, the General job is only shown as a fallback when the user has no other accessible jobs.
     * When false (default), the General job is always included alongside real jobs.
     * This should only be set for pages like Bills where draft-only fallback behavior is desired.
     */
    generalJobFallbackOnly?: boolean;

    /**
     * @deprecated Use includeGlobalJob instead, which uses a real "Global Job" instead of a special legacy job ID to represent a global job
     */
    legacyIncludeGlobalJob?: boolean;

    /**
     * Allows the global docs to appear in the job picker
     */
    allowGlobalDocs?: boolean;

    /**
     * Only set if the page is a job picker router
     */
    isJobPickerRouter?: boolean;
    hideNav?: boolean;
    /**
     * If true, when "All Jobs" is selected but jobPickerData is not loaded,
     * falls back to using selectedJobIds from localStorage (getJobPickerState).
     * This is needed for SUBS users where the job picker may not be shown/loaded.
     */
    useLocalStorageFallbackJobIds?: boolean;
    /**
     * If true, when no job has been selected (or the previously selected job is no longer
     * available), the page defaults to "All Jobs" instead of showing a "No Job selected"
     * empty state. Use this for pages where rendering bills/records across all jobs is a
     * better default than blocking the user behind a job picker.
     */
    defaultToAllJobs?: boolean;
    /**
     * If this prop is provided, when the selected job changes the page will redirect to the provided URL
     * Use for handling cases where a user may be viewing a job specific url, but is able to change the job picker.
     * Example: "/app/Owner/Estimate" would be appended to https://buildertrend.net
     */
    redirectOnJobChangeURL?: string;
    allJobsText?: string;
}

interface IListPageState {
    jobPickerData: JobPickerLoadResponse | undefined;
    headerInfo: IHeaderInfoResponse | undefined;
    selectedJobId: number;
    templatesOnly: boolean;
    sortOption: JobSortOptions;
    keywordSearch: string;
    selectedJobName: string;
    jobFilters: FilterEntity;
    collapsed: boolean;
    selectMode: JobPickerSelectModes;
    selectedFilters?: SelectedFilterItem[];
    selectedFilterId?: number;
    areSavedFiltersShowing: boolean;
    isFrozenBuilderNoticeVisible: boolean;
    frozenBuilderUserOverride?: number;
    isAccountClosed: boolean;
    isJobPickerLoading: boolean;
    loginPortalType: BTLoginTypes;
    keyInnerComponentUpdate: number; // temp solution to update inner component until we can start to leverage React Query
}

class ListPageLayoutInternal extends PureComponent<IListPageLayoutInternalProps, IListPageState> {
    static defaultProps = {
        showHeaderInfo: true,
        jobPickerHandler: new JobPickerHandler(),
        jobHandler: new JobHandler(),
        filterHandler: new FilterHandler(),
        emptyStateHandler: new EmptyStateHandler(),
        showJobPicker: true,
        jobPickerSelectMode: JobPickerSelectModes.Multiple,
    };

    state: Readonly<IListPageState> = {
        jobPickerData: undefined,
        headerInfo: undefined,
        selectedJobId: getSelectedJobId(this.props.builderInfo?.builderId ?? 0),
        templatesOnly: getJobPickerState().isTemplateMode,
        sortOption: JobSortOptions.Alphabetic,
        keywordSearch: getJobPickerState().keywordSearch,
        selectedJobName: "",
        jobFilters: jobFilters,
        // todo remove  portal check
        collapsed: getCollapsed() && isInPortal({ builder: true }),
        selectMode: this.props.jobPickerSelectMode!,
        selectedFilters: undefined,
        areSavedFiltersShowing: false,
        isFrozenBuilderNoticeVisible: false,
        isAccountClosed: false,
        isJobPickerLoading: false,
        loginPortalType: getLoginTypeFromCurrentPortal(),
        keyInnerComponentUpdate: 1,
    };

    static contextType = AppContext;
    declare context: React.ContextType<typeof AppContext>;

    private componentLoadTime: number | null = null;

    componentDidMount = async () => {
        // eslint-disable-next-line deprecate/member-expression
        const qsParams: queryString.ParsedQuery<string> = queryString.parse(window.location.search);

        await this.handleJobpickerJobIdQSParam();

        if (!qsParams.filesUploaded && !this.props.keepQueryParams) {
            clearQueryString();
        }

        // We also want to fetch this information to be available to use on the owner portal even it the picker itself is not shown.
        if (
            this.state.loginPortalType === BTLoginTypes.OWNER ||
            (this.props.showJobPicker && this.props.jobPickerDisplayMode)
        ) {
            await this.setupJobPicker();
            if (this.state.selectedFilters) {
                const selectedJobStatus = this.state.selectedFilters.find(
                    (item) => item.key === (JobPickerFilterTypes.Other as string),
                );
                if (selectedJobStatus) {
                    this.jobStatusFilter =
                        selectedJobStatus.selectedValue as JobStatusFilterTypes[];
                }
            }
        }
        this.componentLoadTime = new Date().getTime();
    };

    componentDidUpdate = async (prevProps: IListPageLayoutInternalProps) => {
        this.checkForNewVersion(prevProps.path);
        const redirectToSetupChanged =
            this.props.userInfo?.shouldRedirectToSetupExperience !==
            prevProps.userInfo?.shouldRedirectToSetupExperience;
        const builderIdChanged =
            prevProps.builderInfo?.builderId !== this.props.builderInfo?.builderId;
        const showJobPickerChanged = prevProps.showJobPicker !== this.props.showJobPicker;
        const generalJobChanged =
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            prevProps.legacyIncludeGlobalJob !== this.props.legacyIncludeGlobalJob ||
            prevProps.includeGeneralJob !== this.props.includeGeneralJob;
        const globalDocsChanged = prevProps.allowGlobalDocs !== this.props.allowGlobalDocs;
        const jobPickerSelectModeChanged =
            prevProps.jobPickerSelectMode !== this.props.jobPickerSelectMode;
        const fromGlobalDocs =
            (prevProps.jobPickerDisplayMode === JobPickerDisplayModes.Documents &&
                this.props.jobPickerDisplayMode === JobPickerDisplayModes.Videos) ||
            (prevProps.jobPickerDisplayMode === JobPickerDisplayModes.Videos &&
                this.props.jobPickerDisplayMode === JobPickerDisplayModes.Documents);
        const isJobPickerRouterChanged =
            prevProps.isJobPickerRouter !== this.props.isJobPickerRouter;

        if (redirectToSetupChanged) {
            const { userInfo } = this.props;
            if (userInfo?.shouldRedirectToSetupExperience && !userInfo?.isImpersonatingBuilder) {
                window.location.assign(routes.appBase + routes.builderSetup.setup);
            }
        } else if (builderIdChanged) {
            await this.setupJobPicker();
        } else if (
            showJobPickerChanged ||
            generalJobChanged ||
            globalDocsChanged ||
            jobPickerSelectModeChanged ||
            fromGlobalDocs ||
            isJobPickerRouterChanged
        ) {
            // set up job picker filter if it hasn't been loaded yet
            const shouldSetupJobpicker = !this.state.jobPickerData && !this.state.selectedFilters;
            this.resetJobIdForSpecialJobs();
            await this.handleJobPickerReload(this.props.jobPickerSelectMode!, shouldSetupJobpicker);
        }
    };

    private checkForNewVersion = (prevPath?: string) => {
        const newAppDetectedDate = BTLocalStorage.get("bt-string-newAppDetectedDate");
        if (newAppDetectedDate && this.props.appDefaultInfo?.deployRefreshRedirectWaitTime) {
            const detectedTime = new Date(newAppDetectedDate).getTime();
            const currentTime = Date.now();

            if (
                prevPath !== this.props.path &&
                this.componentLoadTime &&
                currentTime - detectedTime >=
                    this.props.appDefaultInfo?.deployRefreshRedirectWaitTime &&
                this.componentLoadTime < detectedTime
            ) {
                window.location.reload();
            }
        }
    };

    private resetJobIdForSpecialJobs = () => {
        const { selectedJobId } = this.state;
        const isNotGlobalDocs =
            !this.props.allowGlobalDocs && selectedJobId === (JobIdTypes.GlobalDocs as number);
        const isNotLegacyGlobalJob =
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            !this.props.legacyIncludeGlobalJob &&
            selectedJobId === (JobIdTypes.GlobalJob as number);

        if (isNotGlobalDocs || isNotLegacyGlobalJob) {
            this.setState({
                selectedJobId: JobIdTypes.NoJobs,
            });
        }
    };

    private handleJobpickerJobIdQSParam = async () => {
        // Set Jobpicker JobID if the jobID in the query string does not equal the selected jobID
        // in BTLocalStorage

        // eslint-disable-next-line deprecate/member-expression
        const qsParams: queryString.ParsedQuery<string> = queryString.parse(window.location.search);

        if (qsParams.jobId) {
            const currentJobPickerState: IJobPickerPersistantState = BTLocalStorage.get(
                "bt-object-dangerousJobPickerState",
            );
            const newJobID: number = Number(qsParams.jobId);

            if (
                !(
                    currentJobPickerState.selectedJobIds.length === 1 &&
                    currentJobPickerState.selectedJobIds[0] === newJobID &&
                    currentJobPickerState.selectedJobIds.includes(newJobID) &&
                    isNaN(newJobID)
                ) &&
                this.props.builderInfo?.builderId &&
                this.state.selectedJobId !== newJobID
            ) {
                this.setState({
                    ...this.state,
                    selectedJobId: newJobID,
                });

                // Keep legacy pages (non-SPA) in sync
                // non-SPA storage
                const keywordSearch = this.state.keywordSearch;
                const filteredJobs = this.getSelectedFilteredJobs(newJobID, keywordSearch);
                if (!this.state.templatesOnly) {
                    const selectedJobStatus = getSelectedValue(
                        (await this.props.jobHandler!.get(newJobID)).jobInfo.jobStatus,
                    );
                    if (selectedJobStatus === JobStatusTypes.Closed) {
                        this.jobStatusFilter = [JobStatusFilterTypes.Closed];
                    } else if (selectedJobStatus === JobStatusTypes.Presale) {
                        this.jobStatusFilter = [JobStatusFilterTypes.Presale];
                    } else if (selectedJobStatus === JobStatusTypes.Warranty) {
                        this.jobStatusFilter = [JobStatusFilterTypes.Warranty];
                    } else {
                        this.jobStatusFilter = [JobStatusFilterTypes.Open];
                    }
                }
                await this.getSetJobDetailsResponse(
                    {
                        jobId: newJobID,
                        builderId: this.props.builderInfo.builderId,
                    },
                    filteredJobs,
                    false,
                    {
                        keywordFilter: keywordSearch,
                        otherFilter: this.state.templatesOnly
                            ? "Templates"
                            : this.jobStatusFilter.toString(),
                        leadStatusFilter: "",
                        jobGroupFilter: [],
                        projectManagerFilter: [],
                        selectedBuilderFilter: this.props.builderInfo.builderId,
                    },
                    this.state.templatesOnly,
                );
            }
        }
    };

    private async getJobPickerInfo(): Promise<{
        filters: FilterEntity;
        filterString: string;
    }> {
        const jobPickerState = getJobPickerState();
        const filters = await this.loadFilters();
        const defaultFilter = filters.savedFilters.find((x) => x.isDefault);
        const storedFilter = this.state.templatesOnly
            ? jobPickerState.templateFilters
            : jobPickerState.filters;
        const filterString = storedFilter || (defaultFilter && defaultFilter.value) || "";
        return { filters, filterString };
    }

    private async setupJobPicker(
        selectMode?: JobPickerSelectModes,
        rethrowLoadFailure: boolean = false,
    ): Promise<void> {
        try {
            const { filters, filterString } = await this.getJobPickerInfo();
            const selectedFilters = getSelectedFilterItems(filters, filterString);

            await this.loadJobs(filterString, filters, selectMode);

            this.setState({
                selectedFilters,
            });
        } catch (e) {
            // The caller wants to recover from a bad selection itself rather than show an error
            if (rethrowLoadFailure && !(e instanceof NoAccessError)) {
                throw e;
            }
            if (e instanceof NoAccessError) {
                if (e.reason === ForbiddenReason.NotConnected) {
                    handleNoJobAccessError();
                } else if (e.reason === ForbiddenReason.SubscriptionInactive) {
                    handleSubscriptionInactiveError();
                    return;
                }

                // User has lost access to the job, fallback to no job selected
                // Preemptively clear any remembered job or builder for No Jobs selection.
                // Without this, LPL is initialized with stale JobPicker state, causing a redirect loop.
                BTLocalStorage.remove("bt-object-selectedJobsiteIdWithBuilder");
                BTLocalStorage.remove("bt-number-selectedBuilderId");
                removeJobPickerState();

                // Handle updating JobPicker state and redirecting to appropriate landing page for portal type.
                window.location.assign(
                    getBaseRoute() +
                        routes.jobPicker.selectJobLink(
                            JobIdTypes.NoJobs,
                            routes.landingPage.getLinkForLoginType(
                                this.state.loginPortalType,
                                false,
                            ),
                        ),
                );
            } else {
                // Handle other errors
                showAPIErrorMessage(e);
            }
        }
    }

    private jobStatusFilter: JobStatusFilterTypes[] = [JobStatusFilterTypes.Open];
    private setJobAPIHandlerResult?: IAPIHandlerResult<JobPickerSetResponse>;

    // ToDo: When the app is all SPA rename this method and remove legacy rest call or just replace the call with the set of localstorage
    private getSetJobDetailsResponse(
        selectedJobId: IApiJobBuilderRequest,
        selectedJobIdsRequest: IApiJobBuilderRequest[],
        persistFilters: boolean,
        filtersData: IApiJobFilterRequest,
        isTemplateMode: boolean,
    ): Promise<JobPickerSetResponse | undefined> {
        this.clearSetJobAPIHandlerResult();

        let selectedJobIds = selectedJobIdsRequest;
        if (
            selectedJobIdsRequest.length === 1 &&
            isSpecialJob(
                selectedJobIdsRequest[0].jobId,
                selectedJobIdsRequest[0].jobStatus ?? JobStatus.Open,
            )
        ) {
            selectedJobIds = [];
        }

        // non-SPA storage
        this.setJobAPIHandlerResult = this.props.jobPickerHandler!.set(
            selectedJobId,
            selectedJobIds,
            persistFilters,
            filtersData,
            false,
        );

        // SPA storage
        let jobIds = getSelectedJobIdsForJobPicker(
            selectedJobId.jobId,
            selectedJobIds,
            this.state.selectMode,
        );
        if (typeof jobIds === "number") {
            jobIds = [jobIds];
        }
        const jobPickerState = getJobPickerState();
        jobIds = jobIds ?? [];
        // Preserve the jobs filter when switching to template mode
        let filterString: string;
        if (isTemplateMode) {
            filterString = jobPickerState.filters;
        } else {
            const filters = this.tryParseFilterString(jobPickerState.filters);
            filters[JobPickerFilterTypes.Other] = filtersData.otherFilter;
            filterString = JSON.stringify(filters);
        }
        const storageValue = {
            ...jobPickerState,
            keywordSearch: filtersData.keywordFilter,
            filters: filterString,
            isTemplateMode: isTemplateMode,
            selectedJobIds: jobIds,
            isAllJobsSelected:
                jobIds.length > 1 || selectedJobId.jobId === (JobIdTypes.AllJobs as number),
        };
        setJobPickerState(storageValue);
        return this.setJobAPIHandlerResult.response;
    }

    private tryParseFilterString = (filter: string) => {
        try {
            return JSON.parse(filter);
        } catch {
            return {};
        }
    };

    private clearSetJobAPIHandlerResult() {
        if (this.setJobAPIHandlerResult) {
            this.setJobAPIHandlerResult.cancel();
            this.setJobAPIHandlerResult = undefined;
        }
    }

    private getSelectedFilteredJobs(jobID: number, keywordSearch: string) {
        const selectedFilteredJobs = filterJobsList(
            this.state.jobPickerData?.jobs ?? [],
            // If we can't show the job picker, users can't edit the keyword search. Therefore, default to ""
            this.props.showJobPicker ? keywordSearch : "",
            this.state.templatesOnly,
            this.getAllJobsText(),
        );
        const filteredJobs: IApiJobBuilderRequest[] =
            jobID === (JobIdTypes.AllJobs as number)
                ? selectedFilteredJobs.map((j) => ({
                      jobId: j.jobId,
                      builderId: j.builderId,
                      jobStatus: j.jobStatus,
                  }))
                : [
                      {
                          jobId: jobID,
                          builderId:
                              selectedFilteredJobs.find((j) => j.jobId === jobID)?.builderId ??
                              this.props.builderInfo!.builderId,
                          jobStatus: selectedFilteredJobs.find((j) => j.jobId === jobID)?.jobStatus,
                      },
                  ];

        return filteredJobs;
    }

    private handleOwnerJobChange = async (id: number) => {
        try {
            const jobPickerState = getJobPickerState();
            const storageValue = {
                ...jobPickerState,
                selectedJobIds: [id],
                isAllJobsSelected: false,
            };

            if (id === (JobIdTypes.NoJobs as number)) {
                // Fallback to the default job selection behavior -> Hydrate from backend session storage.
                BTLocalStorage.remove("bt-number-selectedBuilderId");
                removeJobPickerState();
            } else {
                setJobPickerState(storageValue);
                this.setState({ ...this.state, selectedJobId: id });

                const switchResponse = await this.props.jobPickerHandler!.switchOwner(id);
                if (switchResponse.newBuilderId) {
                    BTLocalStorage.set("bt-number-selectedBuilderId", switchResponse.newBuilderId);
                }
            }

            this.handleJobChangeRedirect(false);
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleJobChangeRedirect = (isBuilderJobChange: boolean) => {
        if (this.props.redirectOnJobChangeURL) {
            const newURL = window.location.origin + this.props.redirectOnJobChangeURL;
            window.location.assign(newURL);
        }

        // Below cases are only for handling the owner portal not being SPA yet.
        // If Builder portal, exit out.
        if (isBuilderJobChange) {
            return;
        }

        if (window.location.pathname.endsWith("/SelectJob")) {
            // This is handled by JobPickerRouter, as the redirect URL is in the URL params
        } else {
            const loc = window.location;
            // refresh page without QS (or post values)
            const url = loc.href.split("?")[0];
            window.location.assign(url);
        }
    };

    private handleJobChangeById = async (
        jobId: number,
        _orgId?: number,
        loadMissingJobs?: boolean,
    ) => {
        const { userInfo } = this.props;

        if (userInfo && [BTLoginTypes.OWNER, BTLoginTypes.CONTACT].includes(userInfo.loginType)) {
            await this.handleOwnerJobChange(jobId);
        } else {
            await this.handleJobChangeByIdForJobPicker(jobId, loadMissingJobs);
        }
    };

    private handleJobChangeByIdForJobPicker = async (jobId: number, loadMissingJobs?: boolean) => {
        const { jobPickerData, keywordSearch } = this.state;
        let job = null;

        if (jobPickerData?.jobs) {
            job = jobPickerData.jobs.find((j) => j.jobId === jobId);
        }

        if (jobId === (JobIdTypes.NoJobs as number)) {
            job = new JobPickerJob({
                jobId: JobIdTypes.NoJobs,
                jobName: "No Job selected",
                builderId: this.props.builderInfo!.builderId,
            });
        }

        if (job) {
            await this.handleJobChange(job, keywordSearch);
        } else if (loadMissingJobs) {
            try {
                const jobInfo = await this.props.jobHandler!.get(jobId);
                const status: JobStatus = getSelectedValue(jobInfo.jobInfo.jobStatus);
                const filters = this.setFiltersForMissingJob(this.state.selectedFilters, status);

                await this.getSetJobDetailsResponse(
                    {
                        builderId: this.props.builderInfo!.builderId,
                        jobId: this.state.selectedJobId,
                    },
                    [],
                    false,
                    {
                        keywordFilter: "",
                        otherFilter: this.state.templatesOnly
                            ? "Templates"
                            : this.jobStatusFilter.toString(),
                        leadStatusFilter: "",
                        selectedBuilderFilter: this.props.builderInfo!.builderId,
                    },
                    this.state.templatesOnly,
                );

                const jobData = await this.loadJobs(
                    getFilterStringFromItems(filters),
                    undefined,
                    JobPickerSelectModes.Multiple,
                    JobPickerDisplayModes.JobPickerRouter,
                );
                if (jobData?.jobs) {
                    job = jobData.jobs.find((j) => j.jobId === jobId);
                    if (job) {
                        await this.handleJobChange(job, "");
                    }
                }
            } catch (e) {
                showAPIErrorMessage(e);
            }
        } else {
            void message.error("Could not select job");
        }
    };

    private setFiltersForMissingJob = (
        filters: SelectedFilterItem[] | undefined,
        status: JobStatus,
    ) => {
        if (filters) {
            const orgFilter = filters?.find(
                (f) => f.key === (JobPickerFilterTypes.Other as string),
            );
            let updatedStatusFilter = orgFilter!.selectedValue as number[];
            const expectedStatus = mapJobStatusToJobStatusFilterTypes(status);
            if (
                orgFilter &&
                orgFilter.selectedValue &&
                (orgFilter.selectedValue as number[]) &&
                !(orgFilter.selectedValue as number[]).includes(expectedStatus)
            ) {
                updatedStatusFilter = [...updatedStatusFilter, expectedStatus];
            }

            filters = filters.map((item) => {
                // add the missing status if needed to the "other" (job status)
                if (item.key === (JobPickerFilterTypes.Other as string)) {
                    return new SelectedFilterItem({
                        ...item,
                        selectedValue: updatedStatusFilter,
                    });
                } else {
                    // clear out all other filters
                    return new SelectedFilterItem({
                        ...item,
                        selectedValue: "",
                    });
                }
            });
            this.setState({
                keywordSearch: "",
                selectedFilters: filters,
            });
        }
        return filters;
    };

    private handleJobChange = async (
        job: JobPickerJob,
        keywordSearch: string,
        props?: RouteComponentProps,
    ) => {
        // Update the legacy storage keys and session side selected job ids so state is preserved
        // when switching SPA -> non-SPA. Remove all of this below when deleting the legacy non-SPA
        // job picker (JobPickerWrapper)

        // BEGIN LEGACY BACKWARDS COMPATABILITY CODE
        // eslint-disable-next-line no-restricted-globals
        localStorage.setItem(
            getJobPickerStorageKey(
                LocalStorageKeys.JobPickerKeywordSearch,
                false,
                this.props.builderInfo!.builderId,
            ),
            keywordSearch,
        );
        const filteredJobs = this.getSelectedFilteredJobs(job.jobId, keywordSearch);
        const jobStatusFilterValue = mapJobStatusToJobStatusFilterTypes(job.jobStatus);
        let newFilters = this.state.selectedFilters;
        if (job.jobId > 0 && !this.jobStatusFilter.includes(jobStatusFilterValue)) {
            this.jobStatusFilter.push(jobStatusFilterValue);
            newFilters = newFilters
                ? newFilters.map((item) => {
                      if (item.key === (JobPickerFilterTypes.Other as string)) {
                          return new SelectedFilterItem({
                              ...item,
                              selectedValue: [
                                  ...(item.selectedValue! as number[]),
                                  jobStatusFilterValue,
                              ],
                          });
                      } else {
                          return new SelectedFilterItem({
                              ...item,
                          });
                      }
                  })
                : undefined;
        }

        // We need to set the job in the backend to stay in sync with non-SPA pages.
        const resp = await this.getSetJobDetailsResponse(
            {
                jobId: job.jobId,
                builderId: job.builderId,
            },
            filteredJobs,
            true,
            this.getFilterData(keywordSearch),
            this.state.templatesOnly,
        ).catch(() => {
            // This is for backwards compatability. It's not super important that this call passes,
            // as the worst that would happen is the selected job isn't consistent between SPA
            // and non-SPA pages.
        });
        // END LEGACY BACKWARDS COMPATABILITY CODE

        if (resp) {
            this.setState({
                keywordSearch,
                selectedJobId: job.jobId,
                selectedJobName: job.jobName,
                isJobPickerLoading: false,
                selectedFilters: newFilters,
                jobPickerData: {
                    ...this.state.jobPickerData!,
                    ownerInfo: resp.ownerInfo,
                },
            });
        }
        const shouldRedirectToLandingPage = window.location.pathname.includes(jobDetailRoute.path);
        if (shouldRedirectToLandingPage && props) {
            props.history.push(routes.landingPage.path);
        } else {
            this.handleJobChangeRedirect(true);
        }
    };

    private getPermissions(permissions: JobPickerPermissions): JobPickerPermissions {
        const shouldOverride = window.location.pathname.includes(jobDetailRoute.path);
        if (!shouldOverride) return permissions;
        // override permissions if we're in a jobpicker thats rendered on a flatroute job details page :)
        return {
            ...permissions,
            canViewJobDetails: false,
        };
    }

    private handleBuilderChange = async (newBuilderId: number) => {
        this.setState({
            selectedJobId: JobIdTypes.NoJobs,
            keywordSearch: "",
            keyInnerComponentUpdate: this.state.keyInnerComponentUpdate + 1,
        });

        const { templatesOnly } = this.state;

        // Update the legacy storage keys and session side selected job ids so state is preserved
        // when switching SPA -> non-SPA. Remove all of this below when deleting the legacy non-SPA
        // job picker

        // BEGIN LEGACY BACKWARDS COMPATABILITY CODE
        // todo does this work when all jobs are selected?
        const filterRequest: IApiJobFilterRequest = {
            keywordFilter: "", // TODO: get value from BTSearch for keyword (note: todo from job picker wrapper - do we need to do this?)
            otherFilter: templatesOnly ? "Templates" : this.jobStatusFilter.toString(),
            leadStatusFilter: "",
            jobGroupFilter: [],
            projectManagerFilter: [],
            selectedBuilderFilter: newBuilderId,
        };

        await this.getSetJobDetailsResponse(
            {
                jobId: JobIdTypes.NoJobs,
                builderId: newBuilderId,
            },
            [],
            true,
            filterRequest,
            this.state.templatesOnly,
        );

        // END LEGACY BACKWARDS COMPATABILITY CODE
        await this.context?.reloadGlobalInfo();
        BTLocalStorage.set("bt-number-selectedBuilderId", newBuilderId);
        BTLocalStorage.remove("bt-object-selectedJobsiteIdWithBuilder");
    };

    private closeModal = (matchProps: RouteComponentProps) => {
        matchProps.history.goBack();
    };

    private closeModalRefreshJobs = async (matchProps: RouteComponentProps) => {
        const { history, match } = matchProps;
        const defaultFilter =
            this.state.jobFilters && this.state.jobFilters.savedFilters.find((x) => x.isDefault);
        const initJobPickerState = getJobPickerState();
        const filterString =
            (!initJobPickerState.isTemplateMode && initJobPickerState.filters) ||
            (initJobPickerState.isTemplateMode && initJobPickerState.templateFilters) ||
            (defaultFilter && defaultFilter.value) ||
            "";
        history.replace(match.url);
        await this.loadJobs(filterString);
        this.handleUpdateInnerComponentKey();
    };

    /**
     * This handleUpdateInnerComponentKey method updates a key value that is used to trigger the inner component to
     * completely refresh to keep inner data in sync with data changes.
     * example: when a job name gets changed from job picker this will force the inner component (list page) to update.
     * and refetch the latest data with the new job name.
     * This is a temporary solution to update stale data until we are able to use React Query across the application.
     */
    private handleUpdateInnerComponentKey = () => {
        this.setState((prevState) => {
            return {
                ...prevState,
                keyInnerComponentUpdate: prevState.keyInnerComponentUpdate + 1,
            };
        });
    };

    private handleCollapse = (isCollapsing: boolean) => {
        this.setState({
            collapsed: isCollapsing,
        });
    };

    private handleDismissBanner = async () => {
        const entityType = this.state.templatesOnly
            ? EmptyStateEntity.JobTemplates
            : EmptyStateEntity.Jobs;
        await this.props.emptyStateHandler!.dismissAlert(entityType);
    };

    private getSelectedFilteredJobIds = memoizeOne(
        (
            selectedJobId: number,
            jobPickerData: JobPickerLoadResponse | undefined,
            keywordSearch: string,
            templatesOnly: boolean,
        ) => {
            if (selectedJobId === (JobIdTypes.AllJobs as number)) {
                const selectedFilteredJobs = filterJobsList(
                    jobPickerData?.jobs ?? [],
                    // If we can't show the job picker, users can't edit the keyword search. Therefore, default to ""
                    this.props.showJobPicker ? keywordSearch : "",
                    templatesOnly,
                    this.getAllJobsText(),
                );
                // Return a list of all jobs, but filtering out special jobs
                const filteredJobIds = selectedFilteredJobs
                    .map((j) => j.jobId)
                    .filter((jobId) => jobId > 0);

                // If jobPickerData is not loaded and fallback is enabled, use localStorage selectedJobIds
                if (filteredJobIds.length === 0 && this.props.useLocalStorageFallbackJobIds) {
                    const jobPickerState = getJobPickerState();
                    if (jobPickerState.selectedJobIds?.length > 0) {
                        return jobPickerState.selectedJobIds.filter((jobId: number) => jobId > 0);
                    }
                }

                return filteredJobIds;
            } else if (
                selectedJobId === (JobIdTypes.GlobalJob as number) &&
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                this.props.legacyIncludeGlobalJob
            ) {
                return [JobIdTypes.GlobalJob];
            } else if (
                selectedJobId === (JobIdTypes.GlobalDocs as number) &&
                this.props.allowGlobalDocs
            ) {
                return [JobIdTypes.GlobalDocs];
            } else if (selectedJobId <= 0) {
                return [];
            } else {
                return [selectedJobId];
            }
        },
    );

    private handleSwitchError = (e: any, newUserId: number) => {
        if (
            e instanceof APIError &&
            (e.response.data as { switchBuilderFrozen?: boolean }).switchBuilderFrozen
        ) {
            // Builder frozen, show the frozen screen
            const qsDict = {
                dMethod: "iframe",
                overrideSessionUserLoginID: newUserId,
            };
            // eslint-disable-next-line no-restricted-syntax
            const win = window as unknown as {
                btMaster: {
                    btDialogs: { createAndFireDialog: (opts: Record<string, unknown>) => void };
                };
                QSHandler: {
                    CreateQueryStringFromDictionary: (
                        url: string,
                        dict: Record<string, unknown>,
                    ) => string;
                };
                Global: { PageUrlHandler: { MiscPageUrl: { FrozenBuilderNotice: string } } };
            };
            win.btMaster.btDialogs.createAndFireDialog({
                dBoxMethod: "iframe",
                width: "1200px",
                height: "800px",
                src: win.QSHandler.CreateQueryStringFromDictionary(
                    win.Global.PageUrlHandler.MiscPageUrl.FrozenBuilderNotice,
                    qsDict,
                ),
                showCloseX: "true",
                showLoadingSpinner: "true",
            });
        } else {
            showAPIErrorMessage(e);
        }
    };

    private handleAccountChange = async (items: AccountSwitcherItem[]) => {
        try {
            if (items.length > 1) {
                void this.handleBuilderChange(CommonConstants.EmptyInteger);
                return;
            }
            const resp = await this.props.jobPickerHandler!.switchAccount(items[0].globalUserId);
            if (getLoginTypeFromCurrentPortal() === items[0].loginType) {
                clearJobPickerKeys(this.props.builderInfo!.builderId);
                await this.handleBuilderChange(items[0].builderId);
            } else {
                // Todo: Remove the following when we have our dynamic context so we act like full SPA
                if (resp.refreshUrl) {
                    window.location.assign(resp.refreshUrl);
                } else {
                    window.location.reload();
                }

                // Todo: just call load jobs like the following when we have our dynamic context instead of reloading the page which goes against SPA
                //  this.setState({
                //      selectedJobId: JobIdTypes.NoJobs,
                //      keywordSearch: "",
                //  });

                // Load jobs after we switched accounts to update the job list.
                // const defaultFilter =
                //     this.state.jobFilters &&
                //     this.state.jobFilters.savedFilters.find((x) => x.isDefault);
                // const filterString = (defaultFilter && defaultFilter.value) || "";
                // await this.loadJobs(filterString);
                // todo: Make sure main navigation is updated. Need to create a dynamic context so that we can change the UserInfoContext to update to the new account.
            }
        } catch (e) {
            this.handleSwitchError(e, items[0].globalUserId);
        }
    };

    private handleJobPickerReload = async (
        selectionMode: number,
        shouldSetupJobpicker: boolean,
    ) => {
        if (shouldSetupJobpicker) {
            await this.setupJobPicker();
        } else {
            await this.loadJobs(
                getFilterStringFromItems(this.state.selectedFilters),
                this.state.jobFilters,
                selectionMode,
            );
        }
    };

    private handleTemplateModeChanged = async (
        templatesOnly: boolean,
        matchProps: RouteComponentProps,
        builderID: number,
        initialTemplateTab: TemplateListTabs = TemplateListTabs.MyTemplates,
    ) => {
        const currentBuilderId = this.props.builderInfo?.builderId ?? 0;

        // Nothing is recorded on the way out. Picking a row in the job picker - including
        // "All Jobs" - already stores it for that mode, so the only thing this could add is the
        // selection the picker fell back to when the chosen job was filtered out, which would
        // overwrite the job the user actually picked.

        // Restore what was selected the last time we were in the mode being switched to
        const restoredSelection = getSelectedJobsiteIdWithBuilder(currentBuilderId, templatesOnly);
        const jobIdToSet =
            restoredSelection ?? (templatesOnly ? JobIdTypes.AllJobs : JobIdTypes.NoJobs);

        // This first call only establishes the new mode on the server. It intentionally sends a
        // sentinel rather than a restored job id: the set endpoint verifies access for real ids
        // against the builder id we send, and this call has no real builder id to send. The
        // restored job is confirmed below once the picker has validated it.
        const sentinelJobId = templatesOnly ? JobIdTypes.AllJobs : JobIdTypes.NoJobs;

        const jobIds =
            this.state.jobPickerData!.jobs.length === 1 && sentinelJobId === JobIdTypes.AllJobs
                ? []
                : this.state.jobPickerData!.jobs.map((x) => ({
                      jobId: x.jobId,
                      builderId: x.builderId,
                  }));

        let otherFilter: string;
        if (templatesOnly) {
            otherFilter = "Templates";
        } else {
            otherFilter = getStoredJobsOtherFilter(
                this.jobStatusFilter.toString(),
                this.props.builderInfo?.builderId,
            );
        }

        await this.getSetJobDetailsResponse(
            { builderId: 0, jobId: sentinelJobId },
            jobIds,
            true,
            {
                keywordFilter: this.state.keywordSearch,
                otherFilter,
                leadStatusFilter: "",
                selectedBuilderFilter: builderID,
            },
            templatesOnly,
        );

        // Both destinations render through this same layout, so a route change updates props
        // rather than remounting. Template mode has to be applied to state here or the nav and
        // the job picker would keep rendering the previous mode.
        await new Promise<void>((resolve) => {
            this.setState(
                (prevState) => ({
                    templatesOnly,
                    selectedJobId: jobIdToSet,
                    // A saved filter from the mode we are leaving does not exist in the mode we
                    // are entering
                    selectedFilterId: undefined,
                    keyInnerComponentUpdate: prevState.keyInnerComponentUpdate + 1,
                }),
                resolve,
            );
        });

        // Filters are stored per mode, so reload the picker against the mode we just switched
        // to. The select mode of the route we are about to land on is what the restored job has
        // to be valid for - the current route's mode may differ.
        const destinationSelectMode = templatesOnly
            ? JobPickerSelectModes.Single
            : JobPickerSelectModes.Multiple;
        if (restoredSelection !== null) {
            // The picker rejects a job the user can no longer reach rather than returning a list
            // without it, so a stale restore has to be forgotten and the load retried before it
            // surfaces as an error and leaves the picker showing the previous mode's list.
            try {
                await this.setupJobPicker(destinationSelectMode, true);
            } catch {
                clearSelectedJobsiteIdWithBuilder(templatesOnly);
                await new Promise<void>((resolve) => {
                    this.setState({ selectedJobId: sentinelJobId }, resolve);
                });
                await this.setupJobPicker(destinationSelectMode);
            }
        } else {
            await this.setupJobPicker(destinationSelectMode);
        }

        // loadJobs has now either confirmed the restored job against the new mode's list or
        // fallen back, so it is safe to put a real job in the server session.
        const restoredJob = this.state.jobPickerData?.jobs.find(
            (j) => j.jobId === this.state.selectedJobId,
        );
        if (restoredJob && restoredJob.jobId > 0) {
            try {
                await this.getSetJobDetailsResponse(
                    { jobId: restoredJob.jobId, builderId: restoredJob.builderId },
                    this.getSelectedFilteredJobs(restoredJob.jobId, this.state.keywordSearch),
                    true,
                    this.getFilterData(this.state.keywordSearch),
                    templatesOnly,
                );
                this.setState({ selectedJobName: restoredJob.jobName });
            } catch {
                // Keeping the legacy session in sync is best effort - the picker already shows
                // the right job either way
            }
        }

        matchProps.history.push(
            templatesOnly
                ? routes.template.getListLink(initialTemplateTab)
                : routes.landingPage.getLink(),
        );
    };

    /**
     * TODO: update other APIs to not rely on a selectedJobId to be set in the back end and instead have it passed in from the frontend using browser session or react context.
     * This will then allow users to open a new browser tab and be able to choose a new job to view in the new tab while still being able to see another job in the old tab.
     */
    private loadJobs = async (
        filterString: string,
        jobFiltersParam?: FilterEntity,
        selectMode?: JobPickerSelectModes,
        displayMode?: JobPickerDisplayModes,
    ) => {
        let mode = displayMode ?? this.props.jobPickerDisplayMode;

        // Because we don't show the job picker in the owner portal, the job information
        // is often not set so it's not available unless query it separately.
        // Mainly list pages don't show the job name in their headers without this
        // but that has caused enough issue on its own.
        if (!mode && this.state.loginPortalType === BTLoginTypes.OWNER) {
            mode = JobPickerDisplayModes.Minimal;
        }

        if (!mode) {
            return;
        }

        const selectedMode = selectMode ?? this.state.selectMode;

        // Stale-while-revalidate: only show the loading overlay on the very first
        // picker load. Subsequent reloads (e.g. caused by a route change that flips
        // jobPickerSelectMode between list Multiple and detail Single) keep the
        // current picker rendered and swap in fresh data on completion, avoiding a
        // visible BTLoading flash inside the sidebar.
        this.setState({
            isJobPickerLoading: isJobPickerInitialLoad(this.state.jobPickerData),
        });

        const jobPickerData = await this.props.jobPickerHandler!.get(
            String(this.props.builderInfo!.builderId),
            this.state.selectedJobId,
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            this.props.legacyIncludeGlobalJob ?? false,
            this.props.includeGeneralJob ?? false,
            selectedMode,
            mode,
            filterString,
            this.state.templatesOnly,
            false,
        );

        // Only keep the General job as a fallback when the user has no other accessible jobs
        const { filteredJobs, isGeneralJobFallback } = filterGeneralJob(
            jobPickerData.jobs,
            this.props.generalJobFallbackOnly ?? false,
        );
        jobPickerData.jobs = filteredJobs;

        const jobFilters = jobFiltersParam ?? (await this.loadFilters());

        // If we find that the current selected job is no longer a selected job in the job picker,
        // then we need to reset selectedJobId to NoJobs (or AllJobs for owners as they don't have a true job picker)
        const currentJob = jobPickerData.jobs.find((j) => j.jobId === this.state.selectedJobId);
        let selectedJobId = this.state.selectedJobId;
        if (!currentJob) {
            selectedJobId = getFallbackSelectedJobId(
                getCurrentPortalType() === PortalType.OWNER,
                isGeneralJobFallback,
                this.props.defaultToAllJobs,
            );
            // The stored selection is deliberately left alone. A job missing from the list is
            // usually just filtered out rather than gone, and it should come back once the
            // filter that hides it is cleared. A job the user truly cannot reach is forgotten
            // where the picker rejects it outright, in handleTemplateModeChanged.
        }

        const initJobPickerState = getJobPickerState();
        let selectedJobIds =
            getSelectedJobIdsForJobPicker(selectedJobId, jobPickerData.jobs, selectedMode) ?? [];
        if (typeof selectedJobIds === "number") {
            selectedJobIds = [selectedJobIds];
        }

        const storageValue = {
            keywordSearch: this.state.keywordSearch,
            filters: !this.state.templatesOnly ? filterString : initJobPickerState.filters,
            templateFilters: this.state.templatesOnly
                ? filterString
                : initJobPickerState.templateFilters,
            isTemplateMode: this.state.templatesOnly,
            selectedJobIds: selectedJobIds,
            isAllJobsSelected:
                selectedJobIds.length > 1 || selectedJobId === (JobIdTypes.AllJobs as number),
        };
        setJobPickerState(storageValue);

        // BEGIN LEGACY BACKWARDS COMPATABILITY CODE
        // eslint-disable-next-line no-restricted-globals
        localStorage.setItem(
            getJobPickerStorageKey(
                LocalStorageKeys.CurrentJobPickerFilter,
                this.state.templatesOnly,
                this.props.builderInfo!.builderId,
            ),
            filterString,
        );

        const newJobPickerData = {
            ...jobPickerData,
            jobs: filterJobsList(
                jobPickerData.jobs,
                "",
                this.state.templatesOnly,
                this.getAllJobsText(),
            ),
        };
        this.setState({
            jobFilters: jobFilters,
            headerInfo: jobPickerData.headerInfo,
            jobPickerData: newJobPickerData,
            selectedJobId,
            selectedJobName: currentJob?.jobName ?? "",
            isJobPickerLoading: false,
            selectMode: selectedMode,
        });
        return newJobPickerData;
    };

    private loadFilters = async () => {
        const filterType = this.state.templatesOnly
            ? FilterEntityType.TemplateJobPicker
            : FilterEntityType.JobPicker;
        const selectedJobs = this.getSelectedFilteredJobIds(
            this.state.selectedJobId,
            this.state.jobPickerData,
            this.state.keywordSearch,
            this.state.templatesOnly,
        );
        let selectedJob = selectedJobs.length === 1 ? selectedJobs[0] : null;

        if (selectedJob === JobIdTypes.GlobalJob || selectedJob === JobIdTypes.GlobalDocs) {
            selectedJob = null;
        }

        return await this.props.filterHandler!.get(filterType, undefined, selectedJob);
    };

    private handleKeywordClear = async () => {
        await this.handleKeywordUpdate("");
    };

    private handleKeywordUpdate = async (newKeywordSearch: string) => {
        // BEGIN LEGACY BACKWARDS COMPATABILITY CODE
        // eslint-disable-next-line no-restricted-globals
        localStorage.setItem(
            getJobPickerStorageKey(
                LocalStorageKeys.JobPickerKeywordSearch,
                false,
                this.props.builderInfo!.builderId,
            ),
            newKeywordSearch,
        );

        const filteredJobs = this.getSelectedFilteredJobs(this.state.selectedJobId, "").filter(
            (j) => !isSpecialJob(j.jobId, j.jobStatus ?? JobStatus.Open),
        );

        await this.getSetJobDetailsResponse(
            { builderId: this.props.builderInfo!.builderId, jobId: this.state.selectedJobId },
            filteredJobs,
            false,
            this.getFilterData(newKeywordSearch),
            this.state.templatesOnly,
        );

        // END LEGACY BACKWARDS COMPATABILITY CODE

        this.setState({
            keywordSearch: newKeywordSearch,
        });
        let selectedJobIds =
            getSelectedJobIdsForJobPicker(
                this.state.selectedJobId,
                this.state.jobPickerData?.jobs ?? [],
                this.state.selectMode,
            ) ?? [];
        if (typeof selectedJobIds === "number") {
            selectedJobIds = [selectedJobIds];
        }

        const initJobPickerState = getJobPickerState();
        const filterString = getFilterStringFromItems(this.state.selectedFilters);

        const storageValue = {
            keywordSearch: newKeywordSearch,
            filters: !this.state.templatesOnly ? filterString : initJobPickerState.filters,
            templateFilters: this.state.templatesOnly
                ? filterString
                : initJobPickerState.templateFilters,
            isTemplateMode: this.state.templatesOnly,
            selectedJobIds: selectedJobIds,
            isAllJobsSelected:
                selectedJobIds.length > 1 ||
                this.state.selectedJobId === (JobIdTypes.AllJobs as number),
        };
        setJobPickerState(storageValue);
        await this.loadJobs(getFilterStringFromItems(this.state.selectedFilters));
    };

    private getFilterData = (keywordFilter: string): IApiJobFilterRequest => {
        const jobGroupFilterItem =
            (this.state.selectedFilters?.find(
                (i) => i.key === (JobPickerFilterTypes.Neighborhood as string),
            )?.selectedValue as number[]) ?? [];
        const projectManagerFilterItem =
            (this.state.selectedFilters?.find(
                (i) => i.key === (JobPickerFilterTypes.ProjectManager as string),
            )?.selectedValue as number[]) ?? [];
        const selectedLeadStatusFilters =
            (this.state.selectedFilters?.find(
                (x) => x.key === (JobPickerFilterTypes.LeadStatus as string),
            )?.selectedValue as number[]) ?? [];

        return {
            keywordFilter: keywordFilter,
            otherFilter: this.state.templatesOnly ? "Templates" : this.jobStatusFilter.join(","),
            leadStatusFilter: selectedLeadStatusFilters.join(","),
            jobGroupFilter: jobGroupFilterItem.map((i) => i.toString()),
            projectManagerFilter: projectManagerFilterItem.map((i) => i.toString()),
            selectedBuilderFilter: this.props.builderInfo!.builderId,
        };
    };

    private handleFilterSubmit = async (filterValues: IFilterFormValues, keywordSearch: string) => {
        this.setState(
            {
                keywordSearch,
                selectedFilters: filterValues.items,
                selectedFilterId: filterValues.savedFilter.id,
            },
            async () => {
                const filterString = getFilterString(filterValues);
                // Update the persisted filters. This is so that the jobs list is udpated when creating a new entity with all jobs selected
                const jobIds =
                    this.state.jobPickerData!.jobs.length === 1 &&
                    isSpecialJob(
                        this.state.selectedJobId,
                        this.state.jobPickerData!.jobs[0].jobStatus,
                    )
                        ? []
                        : this.state.jobPickerData!.jobs.map((x) => ({
                              jobId: x.jobId,
                              builderId: x.builderId,
                          }));
                const selectedStatusFilters = filterValues.items.find(
                    (x) => x.key === (JobPickerFilterTypes.Other as string),
                )?.selectedValue;
                if (Array.isArray(selectedStatusFilters)) {
                    this.jobStatusFilter = selectedStatusFilters;
                } else {
                    this.jobStatusFilter = [JobStatusFilterTypes.Open];
                }
                this.setJobAPIHandlerResult = this.props.jobPickerHandler!.set(
                    {
                        jobId: this.state.selectedJobId,
                        builderId: this.props.builderInfo!.builderId,
                    },
                    jobIds,
                    true,
                    this.getFilterData(this.state.keywordSearch),
                    false,
                );
                await this.setJobAPIHandlerResult.response;
                await this.loadJobs(filterString);
                void message.success("Results Updated");
            },
        );
    };

    private handleSavedFiltersUpdated = (jobFilters: FilterEntity) => {
        this.setState({ jobFilters });
    };

    private handleSavedFiltersVisibleChange = (visible: boolean) => {
        this.setState({
            areSavedFiltersShowing: visible,
        });
    };

    private handleSortChanged = async (sortOption: JobSortOptions) => {
        this.setState({ sortOption });
        await this.props.jobPickerHandler!.setSort(sortOption);
    };

    private getSingleSelectedJobGroupName = (): string | undefined => {
        const selectedJobGroups = this.state.selectedFilters?.find(
            (filter) => filter.key === (JobPickerFilterTypes.Neighborhood as string),
        )?.selectedValue as number[] | undefined;

        if (selectedJobGroups?.length !== 1) {
            return undefined;
        }

        return this.state.jobFilters.options[JobPickerFilterTypes.Neighborhood].find(
            (x) => x.value === selectedJobGroups[0],
        )?.title;
    };

    private getSelectedJobName = (): string => {
        if (
            !this.props.allJobsText ||
            this.state.selectedJobId !== (JobIdTypes.AllJobs as number)
        ) {
            return this.state.selectedJobName;
        }

        return this.getSingleSelectedJobGroupName() ?? this.state.selectedJobName;
    };

    private getAllJobsText = (): string | undefined => {
        if (!this.props.allJobsText) {
            return undefined;
        }

        return this.getSingleSelectedJobGroupName();
    };

    private getGeneralJobId = memoizeOne(
        (jobs: JobPickerLoadResponse["jobs"] | undefined): number | undefined => {
            return jobs?.find((j) => j.jobStatus === JobStatus.GeneralJob)?.jobId;
        },
    );

    render() {
        const generalJobId = this.getGeneralJobId(this.state.jobPickerData?.jobs);

        if (!this.props.builderInfo) {
            // redirect to login with return url
            const path = window.location.pathname;
            const expectedLoginType = getLoginTypeFromPortal(
                getPortalTypeForUrl(path) ?? PortalType.ALL,
            );
            const redirectUrl = `/?lpr=${encodeURIComponent(
                getLastPageRequested(expectedLoginType, path),
            )}&reason=${encodeURIComponent(CommonConstants.Login.sessionExpired)}`;

            window.location.replace(redirectUrl);
            return <BTLoading />;
        }

        if (this.props.builderInfo?.loginsDisabled) {
            handleLogout(this.props.appDefaultInfo);
        }

        // We have to pull children out here otherwise it will only render children in Route
        const {
            component: Component,
            jobPickerDisplayMode,
            hideOwnerHeaderInfo,
            envInfo,

            ...rest
        } = this.props;
        /**
         * Filter the jobs list to get array of only the real job ids.
         * To pass into the page body through context for page filtering.
         */
        const filteredJobIds = this.getSelectedFilteredJobIds(
            this.state.selectedJobId,
            this.state.jobPickerData,
            this.state.keywordSearch,
            this.state.templatesOnly,
        );

        const summaryUrl = this.state.templatesOnly
            ? routes.template.getListLink()
            : routes.landingPage.getLinkForLoginType(getLoginTypeFromCurrentPortal(), false);
        const isImpersonatingUser =
            this.props.userInfo?.isImpersonatingBuilder ||
            this.props.userInfo?.isBuilderImpersonatingOwner;
        const jobsInPicker =
            this.state.jobPickerData?.jobs?.filter((j) => j.jobId !== JobIdTypes.AllJobs).length ??
            0;
        const ephRemainingDays = GetEphDaysRemaining(envInfo);

        // When this layout is rendered inside an iframe it is being used as a legacy
        // webforms modal (e.g. the Sub Summary Bill widget opens /app/Subs/Bill/... in an
        // iframe when the full-page conversion flag is off). In that case we must not draw
        // the app chrome (MainNavigation + JobPicker sidebar), otherwise the whole nav shell
        // renders inside the modal. Top-level (non-iframe) routes keep their chrome.
        // todo: this can be removed once all modals are migrated to React.
        const isInIframe = window.self !== window.top;
        const showChrome = !isInIframe;

        return (
            <Route
                {...rest}
                render={(matchProps) => {
                    return (
                        <ListPageTrackingWrapper
                            jobId={this.state.selectedJobId}
                            builderId={this.props.builderInfo?.builderId}
                        >
                            {envInfo?.isPublicEph && ephRemainingDays !== undefined && (
                                <PublicEphBanner />
                            )}
                            <div
                                className={cn("ListPageLayout", {
                                    "ListPageLayout-with-sidebar":
                                        this.props.showJobPicker && showChrome,
                                    "ListPageLayout-sidebar-collapsed":
                                        this.props.showJobPicker &&
                                        showChrome &&
                                        this.state.collapsed === true,
                                })}
                            >
                                {!isImpersonatingUser &&
                                    this.props.userInfo?.globalUserId &&
                                    this.props.userInfo.globalUserId > 0 &&
                                    !this.props.userInfo.hasAcceptedTermsAndPolicy && (
                                        <AcceptTermsAndPolicies
                                            handler={new AcceptTermsAndPoliciesHandler()}
                                            closeHeaderModal={() => {
                                                void this.props.reloadGlobalInfo?.();
                                            }}
                                            {...matchProps}
                                        />
                                    )}

                                {/** Check for builderInfo and builderId before calling MainNavigation
                                 * This allows for page error to forward to login page if there is not a session
                                 * otherwise we get a breaking error in the UI.
                                 */}
                                {this.props.builderInfo?.builderId &&
                                    !this.props.hideNav &&
                                    showChrome && (
                                        <>
                                            <MainNavigation
                                                fromReact
                                                builderId={this.props.builderInfo.builderId}
                                                userId={this.props.userInfo!.globalUserId!}
                                                jobId={this.state.selectedJobId}
                                                generalJobId={generalJobId}
                                                isTemplateMode={this.state.templatesOnly}
                                                loginType={this.state.loginPortalType}
                                                selectedMenuItem={this.props.selectedMenuItem}
                                                className="ListPageLayout--nav"
                                                {...matchProps}
                                            />

                                            <ShowOnPortal
                                                owner
                                                render={() =>
                                                    !hideOwnerHeaderInfo && (
                                                        <OwnerHeaderInfo
                                                            builderId={
                                                                this.props.builderInfo!.builderId
                                                            }
                                                            jobId={getSelectedJobId(
                                                                this.props.builderInfo!.builderId,
                                                            )}
                                                            loadNewJob={
                                                                !this.state.isJobPickerLoading
                                                            }
                                                            setSelectedJobId={
                                                                this.handleJobChangeById
                                                            }
                                                            {...matchProps}
                                                        />
                                                    )
                                                }
                                            />
                                        </>
                                    )}

                                {this.props.showJobPicker && showChrome && (
                                    <div className="ListPageLayout--sidebar">
                                        {this.state.jobPickerData === undefined && (
                                            <ResizableJobPickerLoading />
                                        )}
                                        {this.state.jobPickerData !== undefined &&
                                            jobPickerDisplayMode && (
                                                <>
                                                    <JobPicker
                                                        displayMode={jobPickerDisplayMode}
                                                        jobs={this.state.jobPickerData.jobs}
                                                        selectedJobIds={this.state.selectedJobId}
                                                        onJobPickerJobChanged={(
                                                            job,
                                                            keywordSearch,
                                                        ) =>
                                                            this.handleJobChange(
                                                                job,
                                                                keywordSearch,
                                                                matchProps,
                                                            )
                                                        }
                                                        onJobPickerBuilderChanged={
                                                            this.handleBuilderChange
                                                        }
                                                        permissions={this.getPermissions(
                                                            this.state.jobPickerData.permissions,
                                                        )}
                                                        ownerInfo={
                                                            this.state.jobPickerData.ownerInfo
                                                        }
                                                        templatesOnly={this.state.templatesOnly}
                                                        onTemplateModeChanged={(
                                                            templatesOnly,
                                                            _,
                                                            tab,
                                                        ) =>
                                                            this.handleTemplateModeChanged(
                                                                templatesOnly,
                                                                matchProps,
                                                                this.props.builderInfo!.builderId,
                                                                tab,
                                                            )
                                                        }
                                                        selectMode={this.state.selectMode}
                                                        filters={this.state.jobFilters}
                                                        selectedFilterId={
                                                            this.state.selectedFilterId
                                                        }
                                                        selectedFilters={this.state.selectedFilters}
                                                        onFilterSubmit={this.handleFilterSubmit}
                                                        onSortChanged={this.handleSortChanged}
                                                        sortOption={this.state.sortOption}
                                                        keywordSearch={
                                                            this.props.showJobPicker
                                                                ? this.state.keywordSearch
                                                                : ""
                                                        }
                                                        onKeywordClear={this.handleKeywordClear}
                                                        listMetadata={
                                                            this.state.jobPickerData.listMetadata
                                                        }
                                                        onDismissBanner={this.handleDismissBanner}
                                                        availableAccounts={
                                                            this.state.jobPickerData
                                                                .availableAccounts
                                                        }
                                                        onAccountChange={this.handleAccountChange}
                                                        areSavedFiltersShowing={
                                                            this.state.areSavedFiltersShowing
                                                        }
                                                        onSavedFilterVisibleChange={
                                                            this.handleSavedFiltersVisibleChange
                                                        }
                                                        onSavedFiltersUpdated={
                                                            this.handleSavedFiltersUpdated
                                                        }
                                                        setCollapsed={this.handleCollapse}
                                                        collapsed={this.state.collapsed}
                                                        loading={this.state.isJobPickerLoading}
                                                        width={BTLocalStorage.get(
                                                            "bt-number-sidebarWidth",
                                                        )}
                                                        headerInfo={this.state.headerInfo}
                                                        isConnectedToAccounting={
                                                            this.state.jobPickerData
                                                                .isConnectedToAccounting
                                                        }
                                                        showAccountingOnTimeClock={
                                                            this.state.jobPickerData
                                                                .showAccountingOnTimeClock
                                                        }
                                                        fromReact
                                                        builderId={
                                                            this.props.builderInfo!.builderId
                                                        }
                                                        userId={this.props.userInfo!.globalUserId!}
                                                        jobId={this.state.selectedJobId}
                                                        isTemplateMode={this.state.templatesOnly}
                                                        summaryUrl={summaryUrl}
                                                        allJobsText={
                                                            this.getAllJobsText() ??
                                                            this.props.allJobsText
                                                        }
                                                        {...matchProps}
                                                    />
                                                    <RouteRelative
                                                        path="/JobPickerActions"
                                                        render={(routeProps) => (
                                                            <>
                                                                <RouteJob
                                                                    beforeClose={() =>
                                                                        this.closeModalRefreshJobs(
                                                                            matchProps,
                                                                        )
                                                                    }
                                                                    history={routeProps.history}
                                                                    parentRoute={
                                                                        routeProps.match.path
                                                                    }
                                                                />
                                                                <RouteTemplate
                                                                    modalConfig={{
                                                                        beforeClose: () =>
                                                                            this.closeModalRefreshJobs(
                                                                                matchProps,
                                                                            ),
                                                                        parentRoute:
                                                                            routeProps.match.path,
                                                                    }}
                                                                />
                                                                <RouteTemplateFromTemplate
                                                                    modalConfig={{
                                                                        beforeClose: () =>
                                                                            this.closeModalRefreshJobs(
                                                                                matchProps,
                                                                            ),
                                                                        parentRoute:
                                                                            routeProps.match.path,
                                                                    }}
                                                                />
                                                                <RouteMessageCompose
                                                                    modalConfig={{
                                                                        beforeClose: () =>
                                                                            this.closeModal(
                                                                                matchProps,
                                                                            ),
                                                                        parentRoute:
                                                                            routeProps.match.path,
                                                                    }}
                                                                />
                                                            </>
                                                        )}
                                                    />
                                                </>
                                            )}
                                    </div>
                                )}
                                <ErrorBoundary>
                                    <div className="ListPageLayout--body">
                                        <div className="ListPageLayout--body--content">
                                            {/* todo add ModalLauncher component */}
                                            {/* todo add HeaderAlerter component */}
                                            <MfaEnrollmentNudgeBanner
                                                onEnrollClick={() =>
                                                    optInEveryLoginAndLogout(
                                                        this.props.userInfo?.userId ?? null,
                                                    )
                                                }
                                            />
                                            {(this.state.jobPickerData !== undefined ||
                                                !this.props.showJobPicker) && (
                                                <>
                                                    {this.props.component(
                                                        {
                                                            key: this.state.keyInnerComponentUpdate, // This is a temporary solution to update stale data until we are able to use React Query across the application.
                                                            builderId:
                                                                this.props.builderInfo?.builderId ??
                                                                0,
                                                            jobIds: filteredJobIds,
                                                            generalJobId: generalJobId,
                                                            jobName: this.getSelectedJobName(),
                                                            userId: this.props.userInfo!
                                                                .globalUserId!,
                                                            isTemplateMode:
                                                                this.state.templatesOnly,
                                                            loginType: this.state.loginPortalType,
                                                            jobPickerReady:
                                                                this.state.jobPickerData !==
                                                                undefined,
                                                            setSelectedJobId:
                                                                this.handleJobChangeById,
                                                            isAllJobsSelected:
                                                                this.state.selectedJobId ===
                                                                    JobIdTypes.AllJobs ||
                                                                filteredJobIds.length > 1,
                                                            jobPickerCount: jobsInPicker,
                                                            setJobPickerCollapsed:
                                                                this.handleCollapse,
                                                            selectedFilters:
                                                                this.state.selectedFilters,
                                                        },
                                                        matchProps,
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </ErrorBoundary>
                                <BTModal
                                    data-testid="btModalListPageLayout"
                                    width="1200px"
                                    visible={this.state.isFrozenBuilderNoticeVisible}
                                    beforeClose={() => {
                                        this.setState({
                                            frozenBuilderUserOverride: undefined,
                                            isFrozenBuilderNoticeVisible: false,
                                        });
                                    }}
                                    setPageTitle={false}
                                >
                                    <FrozenBuilderDialog
                                        isAccountClosed={this.state.isAccountClosed}
                                        overrideUserId={this.state.frozenBuilderUserOverride}
                                        {...matchProps}
                                    />
                                </BTModal>
                                <HotkeyDisplaySetup />
                            </div>
                        </ListPageTrackingWrapper>
                    );
                }}
            />
        );
    }
}

const defaultAppProviderHandler = new AppProviderHandler();
export const ListPageLayout: React.FC<IListPageLayoutProps> = ({
    appProviderHandler = defaultAppProviderHandler,
    ...props
}) => {
    return (
        <AppProvider handler={appProviderHandler}>
            <ListPageLayoutContext {...props} />
        </AppProvider>
    );
};

interface IListPageLayoutInternalProps extends IListPageLayoutProps {
    builderInfo?: BuilderInfo | null;
    userInfo?: UserInfo;
    appDefaultInfo?: AppDefaultInfo;
    envInfo?: EnvironmentInfo;
    reloadGlobalInfo?: () => Promise<void>;
}

export const ListPageLayoutContext: React.FC<IListPageLayoutProps> = (props) => {
    const appContext = useContext(AppContext);
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const builderInfo = useContext(BuilderInfoContext);
    const userInfo = useContext(UserInfoContext);
    const envInfo = useContext(EnvironmentInfoContext);

    return (
        <FocusProvider shareFocusWithParent>
            <SpaInfoContext.Provider value={DefaultEnabledSpaInfo}>
                <BTLinkSpaRoutingProvider>
                    <ListPageLayoutInternal
                        {...props}
                        appDefaultInfo={appDefaultInfo}
                        builderInfo={builderInfo}
                        userInfo={userInfo}
                        envInfo={envInfo}
                        reloadGlobalInfo={appContext?.reloadGlobalInfo}
                    />
                </BTLinkSpaRoutingProvider>
            </SpaInfoContext.Provider>
        </FocusProvider>
    );
};

interface IFrozenBuilderDialogProps extends RouteComponentProps {
    isAccountClosed: boolean;
    overrideUserId?: number;
}

interface IListPageTrackingWrapperProps {
    jobId: number | undefined;
    builderId: number | undefined;
    children: React.ReactNode;
}

function ListPageTrackingWrapper({ jobId, builderId, children }: IListPageTrackingWrapperProps) {
    const { Track } = useTracking({
        jobId,
        builderId,
    });

    return <Track>{children}</Track>;
}

export const FrozenBuilderDialog = (props: IFrozenBuilderDialogProps) => {
    const builderInfo = useContext(BuilderInfoContext);
    const userInfo = useContext(UserInfoContext);
    return (
        <FrozenBuilder
            builderName={builderInfo?.name}
            encryptedBuilderId={builderInfo?.encryptedBuilderId!}
            isAdmin={userInfo?.isAdmin!}
            loginType={userInfo?.loginType}
            {...props}
        />
    );
};
